<template>
  <fieldset :id="uniqueId" :title="tooltip" class="x-segment-control">
    <legend v-if="label" class="x-segment-legend" :for="uniqueId">
      {{ label }}
    </legend>
    <ul :class="['controller', [controllerClasses]]">
      <li
        v-for="(segment, index) in segments"
        :id="`${segment.value}-segment`"
        :key="index"
        :data-test="'toggle-' + segment.value"
        class="segment"
      >
        <input
          :id="segment.value"
          type="radio"
          class="segment-input"
          :value="segment.value"
          :checked="segment.selected"
          :name="segment.value"
          :disabled="disabled"
        />
        <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/label-has-for, vuejs-accessibility/no-static-element-interactions -->
        <label
          class="segment-label"
          :for="segment.value"
          @click="segmentClicked(segment.value)"
        >
          <x-icon
            v-if="segment.icon"
            :icon="segment.icon"
            height="14"
            width="14"
          />
          {{ segment.label }}
        </label>
      </li>
    </ul>
  </fieldset>
</template>

<!-- eslint-disable vue/block-lang -->
<script>
/* eslint-disable vue/component-api-style */
import uniqueId from 'lodash/uniqueId'
import xIcon from '@/components/shared/x-icon'
import { getThemeClasses } from '@/lib/theme-utils'

/**
 * @deprecated Use `<ys-segmented-control>` with `<ys-segment>` from the latest `@onxmaps/yellowstone-ui` release instead.
 */
export default {
  name: 'XSegmentControl',
  components: {
    xIcon,
  },
  inject: {
    theme: { default: 'hunt' },
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    appearance: {
      type: String,
      default: 'default',
      validator: function (value) {
        return ['theme', 'default'].indexOf(value) !== -1
      },
    },
    appearanceSize: {
      type: String,
      default: 'standard',
      validator: function (value) {
        return ['standard', 'full-width'].indexOf(value) !== -1
      },
    },
    vertical: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    segments: {
      type: Array,
      required: true,
    },
    tooltip: {
      type: String,
      default: '',
    },
    flat: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disabledCallback: {
      type: Function,
      default: () => {},
    },
  },
  emits: ['change'],
  data() {
    return {
      // eslint-disable-next-line vue/no-unused-properties
      smallView: false,
    }
  },
  computed: {
    uniqueId() {
      return this.id ?? uniqueId('segment-control-')
    },
    controllerClasses() {
      const theme = {
        theme: this.theme,
      }[this.appearance]

      return {
        vertical: this.vertical,
        right: !!this.label,
        flat: this.flat,
        [this.appearanceSize]: true,
        ...getThemeClasses(theme),
      }
    },
  },
  methods: {
    segmentClicked(value) {
      if (this.disabled) {
        this.disabledCallback()
      } else {
        this.$emit('change', value)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '@/styles/system/_type.scss';
@use '@/styles/system/_colors.scss';
@use '@/styles/system/_breakpoints.scss';
@use '@/styles/system/_shadow.scss';

.x-segment-control {
  width: auto;
  padding: 17px 0;
  box-sizing: content-box;
  line-height: 1.5;

  .x-segment-legend {
    @include type.body1;
    min-height: 24px;
    line-height: 35px;
    margin: 0;
    float: left;
  }

  .controller {
    padding: 4px;
    background: colors.$Grey20;
    border-radius: 12px;

    .segment {
      @include type.button3Type;
      display: inline-block;
      width: auto;
      text-align: center;

      &-input {
        opacity: 0;
        position: absolute;
        pointer-events: none;
      }

      &-label {
        color: colors.$Grey80;
        border-radius: 10px;
        padding: 0.8em 1em;
        margin-bottom: 0;
        font-size: 14px;
        cursor: pointer;
      }

      .x-icon {
        margin-right: 5px;
        vertical-align: text-bottom;
        line-height: 0;
        display: inline-flex;
      }
    }

    .segment-input:disabled {
      pointer-events: none;

      & + .segment-label {
        opacity: 0.5;
        cursor: auto;
      }
    }

    .segment-input:checked + .segment-label {
      pointer-events: none;
      background: colors.$BaseWhite;
      color: colors.$Grey100;
      box-shadow: shadow.$Low;
    }

    &.right {
      float: right;
    }

    @mixin vertical-styles {
      display: flex;
      flex-flow: column wrap;

      * {
        display: block;
      }

      .segment {
        &-label {
          border-bottom: 0;
        }
      }
    }
    &.vertical {
      @include vertical-styles;
    }
    @include breakpoints.mobile {
      @include vertical-styles;
    }

    &.full-width {
      display: flex;

      .segment {
        display: flex;
        flex-grow: 1;

        &-label {
          flex-grow: 1;
          text-align: center;
        }
      }
    }

    &.huntTheme .segment-input:checked + .segment-label {
      background-color: colors.$BrandHunt;
      color: colors.$BaseWhite;
    }

    &.backcountryTheme .segment-input:checked + .segment-label {
      background-color: colors.$BrandBackcountry;
      color: colors.$BaseWhite;
    }

    &.offroadTheme .segment-input:checked + .segment-label {
      background-color: colors.$BrandOffroad;
    }

    &.fishTheme .segment-input:checked + .segment-label {
      background-color: colors.$BrandFish;
      color: colors.$BaseWhite;
    }

    &.flat {
      background: none;
      border: 1px solid colors.$Grey30;
      border-radius: 5px;
      padding: 0;
      overflow: hidden;

      .segment-label {
        font-size: 11px;
        font-family: Roboto;
        font-weight: 100;
        min-width: 58px;
        cursor: pointer;
      }

      .segment + .segment .segment-label {
        border-left: 1px solid colors.$Grey30;
        border-radius: 0;
      }

      .segment-input:checked + .segment-label {
        box-shadow: none;
        border-radius: 0;
      }
    }
  }
}
</style>
